import React, { useMemo, useContext, useState } from 'react';
import { css } from '@emotion/react';
import { useTranslation } from '@frontend/i18n';
import { theme } from '@frontend/theme';
import { Table, Heading, ExportIcon } from '@frontend/design-system';
import { InsuranceReportContext } from '../dental-insurance-eligibility-report';
import { getLimitationsTableColConfigs, limitationsTableDefaultColumnKeys } from '../helpers';
import { LimitationTableDataType } from '../types';
import { whiteRoundedBg } from './common-styles';

export const LimitationsTable = React.memo(({ isPrintingReport = false }: { isPrintingReport?: boolean }) => {
  const { limitationsTableData, profileData } = useContext(InsuranceReportContext);
  const [searchText, setSearchText] = useState<string>('');
  const { t } = useTranslation('insuranceEligibilityReport');

  const limitationsTableDataFiltered: LimitationTableDataType[] = useMemo(() => {
    return !!searchText
      ? limitationsTableData.filter((row) => JSON.stringify(row).toLowerCase().includes(searchText.toLowerCase()))
      : limitationsTableData;
  }, [limitationsTableData, searchText]);

  const getFilteredKeysForLimitationsTable = (
    dataArray: LimitationTableDataType[],
    keys: (keyof LimitationTableDataType)[]
  ): (keyof LimitationTableDataType)[] => {
    return keys.filter((key) => dataArray.some((data) => data[key] && data[key] !== '-'));
  };

  const searchHandler = (search: string) => setSearchText(search);

  const limitationsTableColKeys = useMemo(
    () => getFilteredKeysForLimitationsTable(limitationsTableDataFiltered, limitationsTableDefaultColumnKeys),
    [limitationsTableDataFiltered]
  );

  const limitationsTableColConfigs = useMemo(
    () => getLimitationsTableColConfigs(isPrintingReport, limitationsTableColKeys),
    [isPrintingReport, limitationsTableColKeys]
  );

  if (limitationsTableData && limitationsTableData.length > 0) {
    return (
      <div css={[whiteRoundedBg, wrapperStyle]}>
        <Heading level={2}>{t('Coverage Limitations and Exclusions')}</Heading>
        {/* @ts-ignore  value of "hasGlobalSearch" cannot be dynamically passed. */}
        <Table
          colConfig={limitationsTableColConfigs}
          data={limitationsTableDataFiltered}
          globalSearchConfig={{
            searchHandler,
            debounceDelay: 500,
          }}
          tableInstanceId='limitations-table'
          exportConfig={{
            exportFileName: () => `${profileData?.firstName ?? ''}_limitations_data`,
          }}
          tableActions={
            !isPrintingReport
              ? [
                  {
                    Icon: ExportIcon,
                    label: t('Export CSV'),
                    onClick: (tableInstance) => tableInstance.exportData('csv', true),
                  },
                ]
              : []
          }
          {...(!isPrintingReport && {
            isPaginated: true,
            hasGlobalSearch: true,
            hasFilterColumns: true,
          })}
        />
      </div>
    );
  }
  return null;
});

LimitationsTable.displayName = 'LimitationsTable';

const wrapperStyle = css`
  margin: ${theme.spacing(4, 0, 0, 0)};
`;
